import cloudflareAssets from "@/assets/cloudflareAssets";
import type { ApplicationTypes } from "@/modules/application/common/enum";
import type {
  IApplication,
  IOpenApplicationDetailsProps
} from "@/modules/application/common/types";
import { ApplicationActionModals } from "@/modules/application/components";
import { useApplicationColumns } from "@/modules/application/hooks";
import {
  useGetApplicationsQuery,
  useSignApplicationMutation
} from "@/modules/application/services";
import { setApplication, setApplicationRowId } from "@/modules/application/state";
import { INITIAL_PAGE, LOGIN_OTP_COUNT, PageSizeOptions, } from "@/shared/common/constants";
import { CloudflareAsset, ErrorMessages, OperationStatus, TableNames } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, FilterPanel, OTPPopup, Select } from "@/shared/components";
import { daysApart, getTableName } from "@/shared/helpers";
import {
  useAppDispatch,
  useAppSelector,
  useFilters,
  useMediaQuery,
  useNotify,
  useOperationActions,
  useTableHooks,
} from "@/shared/hooks";
import {
  AdvancedTable as Table,
  ColumnSettings,
} from "@ab.uitools/ui-library/components/AdvancedTable";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const MAX_DIFF_DAYS = 2;

interface Props {
  openDetails: (data: IOpenApplicationDetailsProps) => void;
}

export const ApplicationHistory = ({ openDetails }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    showErrorAlertNotification,
    clearSnackbarNotification,
    showSuccessSnackbar,
    showSnackbar,
  } = useNotify();
  const { filters, setFilters, defaultFilters } = useFilters();
  const snackbarNotification = useAppSelector((state) => state.notification.snackbarNotification);
  const activeItem = useAppSelector((state) => state.common);
  const application = useAppSelector((state) => state.applications.application);
  const rowId = useAppSelector((state) => state.applications.rowId);
  const [applicationId, setApplicationId] = useState<string>("");
  const [isOtpModalOpen, setIsOtpModalOpen] = useState<boolean>(false);
  const [isCantSignModalOpen, setIsCantSignModalOpen] = useState<boolean>(false);
  const [applicationHistory, setApplicationHistory] = useState<IApplication[]>([]);
  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false);

  const {
    page,
    pageSize,
    tableRef,
    hasNextPage,
    setHasNextPage,
    onNextPage,
    onPrevPage,
    onPageSizeChange,
    onPageIndexReset,
    updateHasNextPage,
  } = useTableHooks<IApplication>();

  const {
    data: list,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetApplicationsQuery({
    pageIndex: page,
    pageSize,
    ...filters,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const [signApplication, { isLoading }] = useSignApplicationMutation();

  const {
    actions,
    isOpenSendToEdit,
    setIsOpenSendToEdit,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
  } = useOperationActions();

  const onSign = (application: IApplication) => {
    const newDate = new Date();
    const creationDate = new Date(application.creationDate);
    const diffDays = daysApart(newDate, creationDate);

    if ((application.state.applicationStatus === OperationStatus.Created ||
        application.state.applicationStatus === OperationStatus.PartiallySigned) &&
      diffDays < MAX_DIFF_DAYS
    ) {
      setApplicationId(application.id);
      setIsOtpModalOpen(true);
    } else {
      setIsCantSignModalOpen(true);
    }
  };

  const handleChangeToken = (token: string) => {
    if (token.length === LOGIN_OTP_COUNT) {
      signApplication({
        id: applicationId,
        otp: token,
      })
        .unwrap()
        .then(() => {
          setIsOtpModalOpen(false);
          refetch();
          showSuccessSnackbar(t("alerts.applicationHasBeenSuccessfullySigned"));
        })
        .catch((err) => {
          showErrorAlertNotification(err);
        });
    }
  };

  const closeMenu = () => {
    dispatch(setApplicationRowId(null));
    setIsOpenMenu(false);
  };

  const onSetRowData = (data: IApplication, index: string) => {
    dispatch(setApplication({ application: { ...data, index } }));
    dispatch(setApplicationRowId({ rowId: data.id }));
  };

  const openMenu = (data: IApplication, index: string) => {
    onSetRowData(data, index);
    setIsOpenMenu(true);
  };

  const onRowClick = (data: IApplication, index: string) => {
    onSetRowData(data, index);
    openDetails({
      id: data.id,
      applicationType: data.applicationType as ApplicationTypes,
    });
  };

  const isDesktop = useMediaQuery();
  const tableName = getTableName(TableNames.ApplicationHistory);

  const columns = useApplicationColumns({
    t,
    actions,
    rowId,
    onSign,
    isDesktop,
    closeMenu,
    openMenu,
    isOpenMenu,
  });

  useEffect(() => {
    if (!list) return;

    if (list?.length || (!list?.length && page === INITIAL_PAGE)) {
      setApplicationHistory(list);
      updateHasNextPage(list.length);
    } else if (!list?.length && page !== INITIAL_PAGE) {
      onPrevPage();
      setHasNextPage(false);
    }
  }, [list]);

  useEffect(() => {
    if (snackbarNotification?.message) {
      showSnackbar({
        message: snackbarNotification?.message || "",
        type: snackbarNotification?.type,
      });
    }
  }, [snackbarNotification]);

  useEffect(() => {
    if (
      activeItem.applicationId &&
      activeItem.activeTransactionType &&
      list?.length
    ) {
      const activeRowId = list?.findIndex(
        (t) => t.id === activeItem.applicationId
      );
      const activeRow = list?.find(
        (t) => t.id === activeItem.applicationId
      );
      dispatch(setApplicationRowId({ rowId: activeRow?.id }));
      dispatch(setApplication({
        application: { ...activeRow as IApplication, index: `${activeRowId}` }
      }));
      openDetails({
        id: `${activeItem.applicationId}`,
        applicationType: activeItem.activeTransactionType as ApplicationTypes,
      });
    }
  }, [activeItem, list]);

  useEffect(() => {
    return () => {
      clearSnackbarNotification();
      dispatch(setApplication(null));
      dispatch(setApplicationRowId(null));
    };
  }, []);

  return (
    <>
      <div className="page-body full-width flexbox justify-content--center">
        <div
          className={classNames("advanced_table-wrapper", {
            "table-full": applicationHistory.length,
            "table-empty": !applicationHistory.length,
          })}>
          <div className="full-width pb-24">
            <Heading className="content-title" weight="bolder" size="xsmall">
              {t("applicationHistoryTable.title")}
            </Heading>
          </div>
          <Table
            data={applicationHistory || []}
            withSelect
            isActionsVisible={!isDesktop}
            columns={columns}
            isLoading={isFetching || !tableName}
            onRowClick={(row) => onRowClick(row.original, row.id)}
            activeRowId={rowId ? `${application?.index}` : ""}
            tableSettings={{
              tableName: tableName || "",
              persistColumnSettings: "localStorage"
            }}
            hasError={isError}
            emptyTitle={
              isError ?
                (error as IError)?.data?.warnings?.[0] ||
                t(ErrorMessages.TryAgainShortly) :
                t("general.noData")
            }
            emptySubTitle={
              !isError ? t("general.noDataAvailable") : ""
            }
            emptyIllustration={cloudflareAssets[CloudflareAsset.EmptyContent]}
            renderHeader={(table) => (
              <div
                className="advanced-table__header flexbox justify-content--between align-items--center py-16 px-24">
                <FilterPanel
                  filters={filters}
                  setFilters={setFilters}
                  resetPagination={onPageIndexReset}
                  defaultFilters={defaultFilters}
                />
                <div className="flexbox">
                  <ColumnSettings
                    table={table}
                    allToggleText={t("general.all")}
                    menuIconTooltipText={t("general.settings")}
                    tooltipText={t("general.columnCantBeHidden")}
                  />
                </div>
              </div>
            )}
            renderFooter={(table) => {
              tableRef.current = table;
              return (
                <div className="advanced-table__footer">
                  <div className="advanced-table__pagination">
                    <Select
                      setSelectedValue={(value) => onPageSizeChange(value as string)}
                      selectedValue={`${pageSize}`}
                      options={PageSizeOptions}
                    />
                    <div className="flexbox align-items--center">
                      <Button
                        onClick={onPrevPage}
                        size="medium"
                        type="tertiary"
                        data-testid="transaction-history-prev-page"
                        data-ga-id="transaction-history-prev-page"
                        data-ga-action="Transfers history table previos page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                          ),
                        }}
                        disabled={page === INITIAL_PAGE}
                      />
                      <Button
                        onClick={onNextPage}
                        size="medium"
                        type="tertiary"
                        data-testid="application-history-next-page"
                        data-ga-id="application-history-next-page"
                        data-ga-action="application history table next page button"
                        iconProps={{
                          Component: (props: ISVGIconProps) => (
                            <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                          ),
                        }}
                        disabled={!hasNextPage}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
      <OTPPopup
        togglePopup={setIsOtpModalOpen}
        isOpen={isOtpModalOpen}
        handleChangeToken={handleChangeToken}
        isLoading={isLoading}
      />
      <Modal
        isOpen={isCantSignModalOpen}
        onClose={() => setIsCantSignModalOpen(false)}
        onSubmit={() => setIsCantSignModalOpen(false)}
        className="cant-sign-modal"
        hasCancelBtn={false}
        buttonProps={{
          confirm: {
            buttonText: t("general.ok"),
          },
        }}
        titleProps={{
          title: t("general.isNotSubjectToSign"),
          size: "large"
        }}
      >
      </Modal>
      <ApplicationActionModals
        applicationType={application?.applicationType}
        applicationId={application?.id}
        isOpenSendToEdit={isOpenSendToEdit}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsOpenSendToEdit={setIsOpenSendToEdit}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
      />
    </>
  );
};
